import { Upload, Typography, Button, Tooltip } from "antd";
import CustomModal from "components/CustomModal";
import { SvgIcon } from "assets/icons";
import { t } from "i18next";
import { useGeneratePromotionPreview, useGetImportFileCampaign } from "hooks/campaign";
import { useState } from "react";
import { notify } from "utils/helperFuncs";

import ImportErrorModal from "./ImportErrorModal";

function ImportExcelModal({
  campaignType,
  onImportSuccess,
  children,
  title = "Upload file thông tin khuyến mãi",
  width = 500,
}) {
  const { handleGetImportFile } = useGetImportFileCampaign();
  const { handleGeneratePreview } = useGeneratePromotionPreview();
  const [importFile, setImportFile] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorData, setErrorData] = useState({
    errorReason: "",
    actions: [],
    errorFileURL: "",
  });

  const uploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    beforeUpload: (file) => {
      const isExcel =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xlsx");
      if (!isExcel) {
        return Upload.LIST_IGNORE;
      }
      const isLt500K = file.size / 1024 < 500;
      if (!isLt500K) {
        return Upload.LIST_IGNORE;
      }
      setImportFile(file);
      return false;
    },
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;
    try {
      const response = await handleGeneratePreview({
        file: importFile,
        type: campaignType,
      });
      const previewData = response?.data?.promotionCampaign?.generatePromotionPreview;

      if (previewData?.status === "SUCCESS" || previewData?.status === "SUCCEED") {
        const tierGroups = previewData.promotionCampaignTierGroups;
        if (tierGroups && tierGroups.length > 0) {
          onImportSuccess && onImportSuccess(tierGroups);
          setImportFile(null);
          setModalVisible(false);
          notify.success({ message: "Import file thành công" });
        }
      } else if (previewData?.status === "FAILED") {
        setErrorData({
          errorReason: previewData?.errorReason || "Nhập file thất bại",
          actions: previewData?.actions || ["BACK", "RE_UPLOAD_FILE"],
          errorFileURL: previewData?.errorFileURL || "",
        });
        setErrorModalVisible(true);
      } else {
        const errorMsg = previewData?.errorReason || "Nhập file thất bại";
        notify.error({ message: errorMsg });
      }
    } catch (error) {
      notify.error({ message: "Có lỗi xảy ra khi nhập file" });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await handleGetImportFile({
        isExample: true,
        type: campaignType,
      });
      const fileURL = response?.data?.promotionCampaign?.getImportFile?.fileURL;
      if (fileURL) {
        window.open(fileURL, "_blank");
      }
    } catch (error) {
      console.error("Failed to download template:", error);
    }
  };

  const handleErrorCancel = () => {
    setErrorModalVisible(false);
    setImportFile(null);
    setModalVisible(false);
  };

  const handleReUpload = () => {
    setErrorModalVisible(false);
    setImportFile(null);
  };

  return (
    <>
      <CustomModal
        customComponent={
          children || (
            <Tooltip title={"Upload danh sách sản phẩm"} placement="top">
              <Button icon={<SvgIcon.Xlsx width={28} height={28} />} type="text" />
            </Tooltip>
          )
        }
        title={
          <Typography.Title level={4} style={{ textAlign: "center", fontWeight: 700, margin: "0 0 24px 0" }}>
            {title}
          </Typography.Title>
        }
        okeButtonLabel="Xác nhận"
        cancelButtonLabel="Hủy bỏ"
        onOke={handleConfirmImport}
        onCancel={() => setImportFile(null)}
        onOpenChange={setModalVisible}
        open={modalVisible}
        disabled={!importFile}
        width={width}
        footer={false}
        styleBtnGrp={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "24px" }}
      >
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <div style={{ width: "100%", marginBottom: "16px" }}>
            <Upload.Dragger
              {...uploadProps}
              style={{
                backgroundColor: importFile ? "#e6f0ff" : "#f5f9ff",
                borderColor: importFile ? "#0b5fb0" : "#1783e8",
                borderStyle: "dashed",
                borderWidth: "2px",
                borderRadius: "12px",
                padding: "24px",
              }}
            >
              <p
                className="ant-upload-drag-icon"
                style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}
              >
                <SvgIcon.Xlsx style={{ width: "48px", height: "48px" }} />
              </p>
              <p className="ant-upload-text" style={{ color: "#1783e8", fontSize: "14px", marginTop: "12px" }}>
                {importFile ? (
                  <span style={{ fontWeight: "bold" }}>{importFile.name}</span>
                ) : (
                  "Kéo và thả tệp đính kèm để tải lên"
                )}
              </p>
            </Upload.Dragger>
          </div>

          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <Typography.Text type="secondary" style={{ color: "#98a2b3", fontSize: "13px" }}>
              Chọn file tối đa 500kB, định dạng file hỗ trợ xlsx.
              <br />
              Hệ thống hỗ trợ mỗi lần upload tối đa 200 sản phẩm/file.
            </Typography.Text>
          </div>

          <div style={{ textAlign: "center" }}>
            <Typography.Text italic style={{ fontSize: "14px" }}>
              Bạn có thể tải file thông tin mẫu tại{" "}
              <Typography.Link
                onClick={handleDownloadTemplate}
                style={{ color: "#1783e8", textDecoration: "underline", fontWeight: 500, fontStyle: "italic" }}
              >
                ĐÂY
              </Typography.Link>
            </Typography.Text>
          </div>
        </div>
      </CustomModal>

      <ImportErrorModal
        visible={errorModalVisible}
        onCancel={handleErrorCancel}
        onReUpload={handleReUpload}
        errorReason={errorData.errorReason}
        actions={errorData.actions}
        errorFileURL={errorData.errorFileURL}
      />
    </>
  );
}

export default ImportExcelModal;
